import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Modal, Button, HiyaLogo } from "@hiya/ui";
import { signOut } from "../auth/auth-service";
import iconDocumentText from "../assets/icon-document-text.svg";
import iconMessageSearch from "../assets/icon-message-search.svg";
import iconNotification from "../assets/icon-notification.svg";
import iconLogout from "../assets/icon-logout.svg";

const NAV_ITEMS = [
  { label: "Results Review", path: "/dashboard/results", icon: iconDocumentText },
  { label: "Consultations", path: "/dashboard/consultations", icon: iconMessageSearch },
] as const;

interface SidebarProps {
  email: string;
  unreadNotifications?: number;
}

export function Sidebar({ email, unreadNotifications = 0 }: Readonly<SidebarProps>) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showLogout, setShowLogout] = useState(false);

  async function handleLogout() {
    try {
      await signOut();
    } catch {
      // sign-out best-effort; navigate regardless
    }
    navigate("/login", { replace: true });
  }

  return (
    <>
      <aside className="w-[280px] h-screen sticky top-0 border-r border-stroke-primary bg-bg-primary flex flex-col">
        <div className="flex flex-col gap-5 flex-1 overflow-y-auto min-h-0">
          <div className="pt-5 px-5">
            <HiyaLogo width={46} height={40} />
          </div>
          <nav className="flex flex-col gap-3">
            {NAV_ITEMS.map((item) => {
              const isActive = location.pathname.startsWith(item.path);
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  className={`flex items-center gap-3 px-5 py-2 text-base font-medium transition-colors text-left w-[280px] ${
                    isActive
                      ? "border-l-2 border-solid border-l-brand-brown"
                      : ""
                  }`}
                >
                  <div className={`flex flex-1 gap-2 items-center ${isActive ? "text-brand-brown" : "text-text-secondary"}`}>
                    <img src={item.icon} alt="" width={20} height={20} className="shrink-0" />
                    <span>{item.label}</span>
                  </div>
                </button>
              );
            })}
          </nav>
        </div>
        <div className="border-t border-stroke-primary py-2.5">
          <button
            onClick={() => navigate("/dashboard/notifications")}
            className={`flex items-center gap-3 px-5 py-2 text-base font-medium text-left w-[280px] ${
              location.pathname.startsWith("/dashboard/notifications")
                ? "border-l-2 border-solid border-l-brand-brown"
                : ""
            }`}
          >
            <div className={`flex flex-1 gap-2 items-center ${
              location.pathname.startsWith("/dashboard/notifications")
                ? "text-brand-brown"
                : "text-text-secondary"
            }`}>
              <img src={iconNotification} alt="" width={20} height={20} />
              <span>Notifications</span>
            </div>
            {unreadNotifications > 0 && (
              <span className="bg-brand-brown text-text-btn text-xs font-medium rounded-full size-5 flex items-center justify-center">
                {unreadNotifications}
              </span>
            )}
          </button>
          <div className="flex items-center justify-between px-5 py-2">
            <span className="text-base font-medium text-text-primary truncate">
              {email}
            </span>
            <button
              onClick={() => setShowLogout(true)}
              className="shrink-0 hover:opacity-80 transition-opacity"
            >
              <img src={iconLogout} alt="Log out" width={20} height={20} />
            </button>
          </div>
        </div>
      </aside>

      <Modal open={showLogout} title="Log Out" onClose={() => setShowLogout(false)}>
        <p className="text-base text-text-secondary">
          Are you sure you want to log out?
        </p>
        <div className="flex gap-3 mt-2">
          <Button variant="secondary" onClick={() => setShowLogout(false)}>
            No, go back
          </Button>
          <Button onClick={handleLogout}>Yes, log out</Button>
        </div>
      </Modal>
    </>
  );
}
